import { AGENT_TURN_STATUS_ACTIVE, AGENT_TURN_STATUS_DONE, TOOL_CALL_STATUS } from '@kentik/ui-shared/kaizen/constants';

const stepSorter = (a, b) => (a.step_number || 0) - (b.step_number || 0);

// A tool call is settled once it reaches one of these; anything else is still in flight.
const SETTLED_TOOL_STATUSES = new Set([
  TOOL_CALL_STATUS.SUCCESS,
  TOOL_CALL_STATUS.FAILED,
  TOOL_CALL_STATUS.TIMED_OUT,
  TOOL_CALL_STATUS.USER_REJECTED,
  TOOL_CALL_STATUS.APPROVAL_TIMEOUT,
  TOOL_CALL_STATUS.CANCELLED
]);

/**
 * KaizenPersistenceService records a sub-agent step as a non-null `parent_step_id`, and since
 * RAD-673 also as `parent_tool_call_id`, the delegating tool call.
 */
function isSubAgentStep(step) {
  return step.parent_step_id != null && step.agent_name !== 'kaizen';
}

/**
 * Whether a step still has work outstanding. `processing_status` is only trusted when it
 * explicitly says the step is active — a missing value means the payload omitted it, not
 * that the step is running, which would otherwise pin a spinner on completed work forever.
 */
export function isStepInFlight(step) {
  const toolCalls = step.toolCalls || [];

  if (toolCalls.some((tc) => !tc.status || !SETTLED_TOOL_STATUSES.has(tc.status))) {
    return true;
  }

  return AGENT_TURN_STATUS_ACTIVE.includes(step.processing_status);
}

/** Once the turn is settled nothing inside it can still be running, whatever the steps claim. */
function isTurnDone(turn) {
  return AGENT_TURN_STATUS_DONE.includes(turn?.processing_status);
}

export function isSubAgentRunInFlight({ steps, turn }) {
  if (isTurnDone(turn)) {
    return false;
  }
  return (steps || []).some(isStepInFlight);
}

/**
 * Whether a tool-call event is about this call. Calls carry their row `id` from the first
 * event; `tool_tracking_id` restarts every step and can repeat within one, so it's only the
 * fallback for events without an id (approval resume emits those).
 */
export function isSameToolCall(tc, data, { matchName = true } = {}) {
  if (tc.id && data.id) {
    return tc.id === data.id;
  }
  return tc.tool_tracking_id === data.tool_tracking_id && (!matchName || tc.tool_name === data.tool_name);
}

/**
 * Locates the turn/step that owns a given tool call. Every call has its row `id` from its
 * first event. A call without one can only be matched by `tool_tracking_id`, which restarts
 * for every step, so that match is limited to the call's own step and tool.
 */
function findToolCallStep({ sessionModel, toolCall }) {
  const messages = sessionModel?.get('messages') || [];

  const matches = toolCall.id
    ? (tc) => tc.id === toolCall.id
    : (tc) =>
        toolCall.tool_tracking_id != null &&
        tc.tool_tracking_id === toolCall.tool_tracking_id &&
        tc.tool_name === toolCall.tool_name;

  for (const message of messages) {
    for (const turn of message.agentTurn || []) {
      for (const step of turn.steps || []) {
        if (toolCall.id || !toolCall.agent_step_id || step.id === toolCall.agent_step_id) {
          const found = (step.toolCalls || []).find(matches);
          if (found) {
            return { turn, step, toolCall: found };
          }
        }
      }
    }
  }

  return { turn: null, step: null, toolCall: null };
}

const byTrackingId = (a, b) => (a.tool_tracking_id ?? 0) - (b.tool_tracking_id ?? 0);

/**
 * Rows written before RAD-673 have no `parent_tool_call_id`. Parallel delegations to the same
 * agent on one step collided on the step insert then, so at most one of them has a run: the
 * one that didn't fail, else the first. The others failed before their run began.
 */
function isLegacyRunOwner(step, toolCall) {
  const calls = (step.toolCalls || []).filter((tc) => tc.tool_name === toolCall.tool_name).sort(byTrackingId);
  const owner = calls.find((tc) => tc.status !== TOOL_CALL_STATUS.FAILED) || calls[0];
  return owner === toolCall;
}

/**
 * Everything a single sub-agent invocation did, resolved from the delegation tool call the
 * main agent made. Sub-agent tools are named after the agent, so the tool name identifies it.
 */
export function resolveSubAgentRun({ sessionModel, toolCall }) {
  const { turn, step, toolCall: liveToolCall } = findToolCallStep({ sessionModel, toolCall });

  if (!turn) {
    return { steps: [], turn: null, toolCall: liveToolCall };
  }

  const steps = turn.steps || [];
  const run = steps.filter((s) => s.parent_tool_call_id != null && s.parent_tool_call_id === liveToolCall.id);

  if (run.length > 0) {
    return { steps: run.sort(stepSorter), turn, toolCall: liveToolCall };
  }

  const legacyRun = isLegacyRunOwner(step, liveToolCall)
    ? steps.filter(
        (s) => s.parent_tool_call_id == null && s.parent_step_id === step.id && s.agent_name === liveToolCall.tool_name
      )
    : [];

  return { steps: legacyRun.sort(stepSorter), turn, toolCall: liveToolCall };
}

/**
 * The steps the main thread renders. Sub-agent steps are excluded — their work is shown in
 * the sub-agent panel, opened from the delegation tool call that summoned them. A final
 * answer that isn't already last is moved to the end so it reads as the conclusion.
 */
export function getMainThreadSteps(steps) {
  const mainSteps = (steps || [])
    .slice()
    .sort(stepSorter)
    .filter((step) => !isSubAgentStep(step));

  const finalAnswerIdx = mainSteps.findIndex(
    (step) => step.processing_status === 'COMPLETED' && step.parent_step_id === null && step.llm_response?.final_answer
  );

  if (finalAnswerIdx > -1 && finalAnswerIdx < mainSteps.length - 1) {
    const finalStep = mainSteps[finalAnswerIdx];
    return [...mainSteps.slice(0, finalAnswerIdx), ...mainSteps.slice(finalAnswerIdx + 1), finalStep];
  }

  return mainSteps;
}
